// Companion/blocks/OutsideResultCard.tsx
//
// The `save_outside_result` card's body (ORA-1184). ChatActionCard keeps the
// consent mechanics — confirm, Skip, failure, undo, the fold — and swaps its
// header and detail rows for these two parts when the proposal carries the
// dedicated card's fields.
import { useId, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  CaretRight,
  ChartLineUp,
  CheckCircle,
  Info,
  Minus,
  WarningCircle,
  type Icon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  COMPARISON_BAND_LABELS,
  formatOutsideResultDate,
  notUsedAreaCount,
  type OutsideResultCard,
  type OutsideResultComparisonBand,
  type OutsideResultEffect,
  type OutsideResultUsedArea,
} from './outsideResultCardData';

const BAND_ICONS: Record<OutsideResultComparisonBand, Icon> = {
  higher: ArrowUp,
  average: Minus,
  lower: ArrowDown,
};

const EFFECT_ICONS: Record<OutsideResultEffect, Icon> = {
  full: CheckCircle,
  partial: Info,
  none: WarningCircle,
};

/** A small rounded label, the same pill the generic card's "Needs your OK" is. */
const Pill = ({ children }: { children: string }) => (
  <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs text-fg-secondary">
    {children}
  </span>
);

export interface OutsideResultHeaderProps {
  card: OutsideResultCard;
  isSettled: boolean;
}

/**
 * What the result is: the test, the day it was taken, the overall score, and
 * where the numbers came from. It stays when the card settles, so the receipt
 * still says which result was saved.
 */
export const OutsideResultHeader = ({
  card,
  isSettled,
}: OutsideResultHeaderProps) => (
  <div className="flex items-start gap-3">
    <span
      aria-hidden="true"
      className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-fg-secondary"
    >
      <ChartLineUp size={18} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-fg-secondary">
          Save an outside result
        </span>
        {!isSettled ? <Pill>Needs your OK</Pill> : null}
      </div>
      <div className="mt-0.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="m-0! break-words font-heading text-base! font-medium text-fg-primary">
            {card.title}
          </h3>
          {card.takenAt ? (
            <p className="m-0! text-xs text-fg-secondary">
              Taken {formatOutsideResultDate(card.takenAt)}
            </p>
          ) : null}
          {card.examName ? (
            <p
              className="m-0! break-words text-xs text-fg-secondary"
              data-testid="outside-result-exam"
            >
              {/* A result saved for reference does not count toward it. */}
              {card.effect === 'none' ? 'For' : 'Counts toward'} {card.examName}
            </p>
          ) : null}
        </div>
        {card.overall ? (
          <p
            className="m-0! shrink-0 text-right"
            data-testid="outside-result-overall"
          >
            <span className="sr-only">Overall score: </span>
            <span className="block font-heading text-2xl font-semibold leading-none text-fg-primary">
              {card.overall.value}
            </span>
            {card.overall.scaleLabel ? (
              <span className="mt-1 block text-xs text-fg-secondary">
                {card.overall.scaleLabel}
              </span>
            ) : null}
          </p>
        ) : null}
      </div>
      {card.sourceLabel || card.override ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {card.sourceLabel ? <Pill>{card.sourceLabel}</Pill> : null}
          {card.override ? (
            <span
              className="rounded-lg border border-border-subtle px-2 py-0.5 text-xs text-fg-primary"
              data-testid="outside-result-override"
            >
              {card.override}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  </div>
);

/** The one-line verdict, toned by what saving would do to Readiness. */
const EffectBanner = ({
  card,
  repeatNote,
}: {
  card: OutsideResultCard;
  repeatNote: string | null;
}) => {
  const EffectIcon = EFFECT_ICONS[card.effect];

  return (
    <div
      className={cn(
        'mt-3 flex items-start gap-2 rounded-xl border p-3',
        card.effect === 'full' &&
          'border-status-success/40 bg-status-success/10',
        card.effect === 'partial' && 'border-border-subtle bg-surface-muted',
        card.effect === 'none' && 'border-status-warning/40 bg-surface-muted'
      )}
      data-testid="outside-result-effect"
      data-effect={card.effect}
    >
      <EffectIcon
        size={18}
        aria-hidden="true"
        className={cn(
          'mt-px shrink-0',
          card.effect === 'full' && 'text-status-success',
          card.effect === 'partial' && 'text-fg-secondary',
          card.effect === 'none' && 'text-status-warning'
        )}
      />
      <div className="min-w-0">
        <p className="m-0! text-sm font-medium text-fg-primary">
          {card.headline}
        </p>
        {card.reason ? (
          <p className="m-0! mt-1! text-sm text-fg-secondary">{card.reason}</p>
        ) : null}
        {repeatNote ? (
          <p className="m-0! mt-1! text-sm text-fg-secondary">{repeatNote}</p>
        ) : null}
      </div>
    </div>
  );
};

const UsedAreaRow = ({ area }: { area: OutsideResultUsedArea }) => {
  const BandIcon = area.bandVsComparison
    ? BAND_ICONS[area.bandVsComparison]
    : null;

  // The band leads when the report has one; the printed value then moves to
  // the line underneath instead of competing with it.
  const leadsWithValue = !area.bandVsComparison && area.valueText;

  const meta = [
    area.bandVsSelf ? `vs your overall: ${area.bandVsSelf}` : null,
    area.bandVsComparison ? area.valueText : null,
    area.itemCount > 0
      ? `≈${area.itemCount} question${area.itemCount === 1 ? '' : 's'}`
      : null,
  ].filter(Boolean);

  return (
    <li className="min-w-0 rounded-xl border border-border-subtle p-3">
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
        <span className="min-w-0 break-words text-sm font-medium text-fg-primary">
          {area.label}
        </span>
        {area.bandVsComparison && BandIcon ? (
          <Badge variant="outline">
            <BandIcon aria-hidden="true" />
            {COMPARISON_BAND_LABELS[area.bandVsComparison]}
          </Badge>
        ) : leadsWithValue ? (
          <span className="text-sm text-fg-primary">{area.valueText}</span>
        ) : null}
      </div>
      {area.topics.length > 0 ? (
        <ul
          className="m-0 mt-2 flex list-none flex-wrap gap-1 p-0"
          aria-label={`Topics for ${area.label}`}
        >
          {area.topics.map((topic) => (
            <li key={topic.id} className="min-w-0 max-w-full">
              <Badge
                variant="chip-muted"
                className="max-w-full"
                title={topic.name}
              >
                <span className="truncate">{topic.name}</span>
              </Badge>
            </li>
          ))}
          {area.moreTopicCount > 0 ? (
            <li>
              <Badge variant="chip-muted">
                +{area.moreTopicCount}
                <span className="sr-only">
                  {' '}
                  more topic{area.moreTopicCount === 1 ? '' : 's'}
                </span>
              </Badge>
            </li>
          ) : null}
        </ul>
      ) : null}
      {meta.length > 0 ? (
        <p className="m-0! mt-2! text-xs text-fg-secondary">
          {meta.join(' · ')}
        </p>
      ) : null}
    </li>
  );
};

/** Areas the result will not count, one group per reason, folded by default. */
const NotUsedDisclosure = ({
  card,
  isFolded,
}: {
  card: OutsideResultCard;
  isFolded: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const total = notUsedAreaCount(card);

  return (
    <div className="mt-3">
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="-ml-3 h-11 mobile:h-8"
        aria-expanded={isOpen}
        aria-controls={panelId}
        // Folded away under a settled card it is invisible, so it must not
        // take a Tab stop either.
        tabIndex={isFolded ? -1 : undefined}
        onClick={() => setIsOpen((open) => !open)}
      >
        <CaretRight
          aria-hidden="true"
          className={cn(
            'transition-transform duration-200',
            isOpen && 'rotate-90'
          )}
        />
        Not Used ({total})
      </Button>
      <div id={panelId} hidden={!isOpen} className="mt-1 space-y-2">
        {card.notUsedGroups.map((group) => (
          <div key={`${group.reasonCode}:${group.reasonText}`}>
            <h5 className="m-0! text-xs! font-medium text-fg-primary">
              {group.reasonText}
            </h5>
            <p className="m-0! mt-0.5! break-words text-xs text-fg-secondary">
              {group.labels.join(', ')}
            </p>
          </div>
        ))}
        {card.ignoredItemCount > 0 ? (
          <p className="m-0! text-xs text-fg-secondary">
            {card.ignoredItemCount} listed question
            {card.ignoredItemCount === 1 ? '' : 's'} not used yet; the mapped
            area results are used.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export interface OutsideResultDetailsProps {
  card: OutsideResultCard;
  /**
   * The proposal's own sentence, shown only for a repeat of a saved result —
   * where it says what happens to the earlier one. Otherwise it is the
   * headline and reason again.
   */
  repeatNote: string | null;
  /** True once the card settled and this body has folded out of sight. */
  isFolded: boolean;
}

/** Everything under the header; it folds away when the card settles. */
export const OutsideResultDetails = ({
  card,
  repeatNote,
  isFolded,
}: OutsideResultDetailsProps) => {
  const usedHeadingId = useId();
  const hasNotUsed = card.notUsedGroups.length > 0 || card.ignoredItemCount > 0;

  return (
    <div data-testid="outside-result-details">
      <EffectBanner card={card} repeatNote={repeatNote} />

      {card.usedAreas.length > 0 ? (
        <section className="mt-4 @container" aria-labelledby={usedHeadingId}>
          <h4
            id={usedHeadingId}
            className="m-0! text-xs! font-medium text-fg-secondary"
          >
            Counts toward Readiness
          </h4>
          <ul className="m-0 mt-2 grid list-none grid-cols-1 gap-2 p-0 @xl:grid-cols-2">
            {/* One label can be in two sections of a report. */}
            {card.usedAreas.map((area, index) => (
              <UsedAreaRow key={`${index}:${area.label}`} area={area} />
            ))}
          </ul>
        </section>
      ) : null}

      {hasNotUsed ? (
        <NotUsedDisclosure card={card} isFolded={isFolded} />
      ) : null}

      {card.previousOverall.length > 0 ? (
        <p
          className="m-0! mt-2! text-xs text-fg-secondary"
          data-testid="outside-result-history"
        >
          Earlier scores:{' '}
          {card.previousOverall
            .map(
              (point) =>
                `${point.value} (${formatOutsideResultDate(point.takenAt)})`
            )
            .join(', ')}
        </p>
      ) : null}
    </div>
  );
};
